import { Button, Form, Input, Modal, Space, message } from "antd";
import { DELETE_TYPE, SUB_ACCOUNT_STATE } from "@zhihaoscm/meta";
import { ACTIVE_STATE } from "@zhihaoscm/meta/src/subAccount";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { subManagementService } from "@/api/subManagementService";
import { Breadcrumb, ProTable, type ProColumn } from "@/components/common";
import { useAccountSession, useUserSession } from "@/stores/session";
import { SubAccountPermissionDialog } from "./SubAccountPermissionDialog";

interface SubAccountRow {
    account?: { realName?: string; mobile?: string };
    customerEnterprise?: { id: number; accountState?: number };
}
interface InviteForm {
    mobile: string;
}

/** 主账号优先读取代理会员权限；没有代理权限时读取当前用户会员权限。 */
export function hasSubAccountPermission() {
    const account = useAccountSession.getState();
    const proxy = account.proxyMemberPermission as any;
    const member = Object.keys(proxy ?? {}).length ? proxy : useUserSession.getState().userInfo.memberPermission;
    return Number((member as any)?.subAccountNum || 0) > 0;
}

/** 子账号列表页，提供搜索、邀请、编辑、移除和邀请记录入口。 */
export function SubAccountManagementPage() {
    const [inviteOpen, setInviteOpen] = useState(false);
    const [permissionOpen, setPermissionOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm<InviteForm>();
    const navigate = useNavigate();

    /** 无子账号会员权益时统一展示权益提示。 */
    const requirePermission = (action: () => void) => {
        if (!hasSubAccountPermission()) {
            setPermissionOpen(true);
            return;
        }
        action();
    };
    /** 验证手机号后发出邀请，成功后关闭并清空表单。 */
    const invite = async () => {
        const values = await form.validateFields();
        setSubmitting(true);
        try {
            await subManagementService.addSubAccount(values);
            void message.success("邀请成功");
            form.resetFields();
            setInviteOpen(false);
        } catch {
            void message.error("邀请失败，请稍后重试");
        } finally {
            setSubmitting(false);
        }
    };
    /** 移除操作必须二次确认，完成后重新请求当前列表。 */
    const remove = (row: SubAccountRow) =>
        Modal.confirm({
            title: "提示",
            content: "是否删除？删除后此信息不再展示",
            okText: "确定",
            cancelText: "取消",
            onOk: async () => {
                await subManagementService.deleteSubAccount(
                    row.customerEnterprise!.id,
                    DELETE_TYPE.DELETE_ACCOUNT.code
                );
                setRefreshKey(value => value + 1);
            }
        });
    const columns = useMemo<ProColumn<SubAccountRow>[]>(
        () => [
            {
                key: "realName",
                title: "姓名",
                minWidth: 142,
                search: {
                    key: "searchParam",
                    type: "input",
                    placeholder: "请输入姓名或手机号搜索",
                    span: 5,
                    width: 120
                },
                render: (_, row) => (
                    <Button
                        type="link"
                        onClick={() => navigate(`/me/subAccountManagement/detail?id=${row.customerEnterprise?.id}`)}
                    >
                        {row.account?.realName || "--"}
                    </Button>
                )
            },
            { key: "mobile", title: "手机号", minWidth: 142, render: (_, row) => row.account?.mobile || "--" },
            {
                key: "accountState",
                title: "状态",
                width: 100,
                search: {
                    key: "accountState",
                    type: "select",
                    placeholder: "请选择状态",
                    width: 120,
                    options: ACTIVE_STATE.options
                },
                render: (_, row) =>
                    row.customerEnterprise?.accountState === ACTIVE_STATE.ACTIVE.code ? (
                        <span>已激活</span>
                    ) : (
                        <span className="sub-account-expire">已失效</span>
                    )
            },
            {
                key: "operation",
                title: "操作",
                width: 114,
                fixed: "right",
                render: (_, row) => (
                    <Space size={12} className="sub-account-actions">
                        <Button
                            type="link"
                            onClick={() =>
                                requirePermission(() =>
                                    navigate(`/me/subAccountManagement/edit?id=${row.customerEnterprise?.id}`)
                                )
                            }
                        >
                            编辑
                        </Button>
                        <Button type="link" onClick={() => remove(row)}>
                            移除
                        </Button>
                    </Space>
                )
            }
        ],
        [navigate]
    );

    return (
        <section className="sub-account-page section-card">
            <Breadcrumb routes={[{ name: "账号管理" }, { name: "子账号管理" }]} />
            <div className="sub-account-table">
                <ProTable
                    key={refreshKey}
                    title="子账号列表"
                    rowKey={row => row.customerEnterprise?.id ?? 0}
                    columns={columns}
                    initialParams={{
                        state: SUB_ACCOUNT_STATE.CONFIRMED.code,
                        sortKey: "created_time",
                        sortDirection: "DESC"
                    }}
                    request={params => subManagementService.getSubAccountList(params as any)}
                    header={() => (
                        <Space size={12}>
                            <Button type="primary" onClick={() => requirePermission(() => setInviteOpen(true))}>
                                邀请子账号
                            </Button>
                            <Button
                                type="primary"
                                onClick={() => navigate("/me/subAccountManagement/invitationHistory")}
                            >
                                邀请记录
                            </Button>
                        </Space>
                    )}
                />
            </div>
            <Modal
                centered
                className="sub-account-invite-dialog"
                title="邀请子账号"
                width={432}
                open={inviteOpen}
                confirmLoading={submitting}
                okText="提交"
                cancelText="取消"
                onOk={() => void invite()}
                onCancel={() => {
                    setInviteOpen(false);
                    form.resetFields();
                }}
            >
                <Form form={form} labelAlign="right" labelCol={{ flex: "80px" }}>
                    <Form.Item
                        name="mobile"
                        label="手机号"
                        rules={[
                            { required: true, message: "请输入手机号" },
                            { pattern: /^1[3-9]\d{9}$/, message: "请输入正确的手机号" }
                        ]}
                    >
                        <Input placeholder="请输入手机号" />
                    </Form.Item>
                </Form>
            </Modal>
            <SubAccountPermissionDialog open={permissionOpen} onClose={() => setPermissionOpen(false)} />
        </section>
    );
}
